import { observer } from 'mobx-react-lite'
import { Contents, MainPane, Header, Headline } from 'pages/layout/TwoColumn'
import { useBreakoutUser } from 'hooks/profile'
import { getShortAppCommitSha } from 'util/commitSha'
import { ProfileNavigation } from './ProfileNavigation'
import { useEffect, useMemo, useState } from 'react'
import { ProfileInfoSection } from './ProfileInfoSection'
import { Spinner } from 'components/Spinner'
import { ProfileTransactions } from './ProfileTransactions'
import { ProfilePromotions } from './ProfilePromotions'
import { Person } from 'components/icons/Person'
import { UserProfileRole } from '@breakoutlearning/firebase-repository/types'
import { ProfileCubit } from '@breakoutlearning/firebase-repository/cubits/ProfileCubit'
import { PodcastsComponent } from 'pages/student/podcasts/PodcastsPage'
import { ProfilePages } from '../../types/ProfilePages'
import { ProfileSettings } from './ProfileSettings'
import { ProfileScoreMappingDefaults } from './ProfileScoreMappingDefaults'
import { ProfileAvSection } from './ProfileAvSection'
import { useRootStore } from 'hooks/rootStore'
import { Button } from 'components/Button'
import { ProfileViewFeatureFlags } from 'pages/profile/ProfileViewFeatureFlags'
import { ProfileLtiSettings } from 'pages/profile/ProfileLtiSettings'
import { AdminOnlyRing } from 'components/AdminOnlyRing'
import { PageTitle } from 'components/PageTitle'
import { useTranslationTyped } from 'i18n/i18n'
import { useCubitBuilder } from 'hooks/cubits'
import { useRepository } from 'hooks/auth'
import { Tabs } from 'radix-ui'
import { isProdOrBeta } from 'config/environment'

export const ProfilePage = observer(function ProfilePage() {
  const [selectedPage, setSelectedPage] = useState<ProfilePages>(
    ProfilePages.details
  )
  const repository = useRepository()
  const cubit = useCubitBuilder(() => {
    return new ProfileCubit(repository)
  }, [repository])

  const breakoutUser = useBreakoutUser()
  const { tt } = useTranslationTyped()
  const allowLtiSettings = breakoutUser.isAdmin && !isProdOrBeta()

  const commitSha = getShortAppCommitSha()
  const rootStore = useRootStore()

  const roleText = useMemo(() => {
    switch (breakoutUser.profile.data.role) {
      case UserProfileRole.student:
        return tt.profile.role_student()
      case UserProfileRole.ta:
        return tt.profile.role_assistant()
      case UserProfileRole.instructor:
        return tt.profile.role_instructor()
      case UserProfileRole.author:
        return tt.profile.role_author()
      case UserProfileRole.editor:
        return tt.profile.role_editor()
      case UserProfileRole.corre:
        return tt.profile.role_corre()
      case UserProfileRole.admin:
        return tt.profile.role_admin()
    }
  }, [breakoutUser.profile.data.role, tt])

  useEffect(() => {
    const params = rootStore.router.queryParams
    if (!params) return
    if (!params.tab) return
    if (params.tab === ProfilePages.ltiSettings && !allowLtiSettings) {
      return
    }
    // Only instructors can save defaults (BL-4757); hiding the nav entry is
    // not enough because ?tab= selects the tab content directly.
    if (
      params.tab === ProfilePages.scoreMappingDefaults &&
      !breakoutUser.isInstructor
    ) {
      return
    }
    setSelectedPage(params.tab as ProfilePages)
  }, [
    rootStore.router.queryParams,
    allowLtiSettings,
    breakoutUser.isInstructor,
  ])

  const onChangeTab = (value: string) => {
    if (
      value !== ProfilePages.details &&
      value !== ProfilePages.transactions &&
      value !== ProfilePages.avTest &&
      value !== ProfilePages.podcast &&
      value !== ProfilePages.promotions &&
      value !== ProfilePages.scoreMappingDefaults &&
      value !== ProfilePages.settings &&
      value !== ProfilePages.viewFeatureFlags &&
      value !== ProfilePages.ltiSettings
    ) {
      console.error(`Invalid tab: ${value}`)
      return
    }
    if (value === ProfilePages.ltiSettings && !allowLtiSettings) {
      console.error(`Invalid tab: ${value}`)
      return
    }
    if (
      value === ProfilePages.scoreMappingDefaults &&
      !breakoutUser.isInstructor
    ) {
      console.error(`Invalid tab: ${value}`)
      return
    }
    setSelectedPage(value)
  }

  return (
    <MainPane>
      <PageTitle title={tt.profile.headline()} />

      <Header>
        <Headline>{tt.profile.headline()}</Headline>
        <div className="flex items-center">
          <AdminOnlyRing
            onlyShowIf={breakoutUser.isInternal}
            label={tt.roles.internal_only()}
            ringClassName="mr-4"
          >
            <p className="text-title-small text-on-surface-var">
              {tt.profile.version()}:{' '}
              <span
                className="cursor-pointer underline"
                onClick={() => {
                  window.open(
                    `https://github.com/breakoutlearning/cases/commit/${commitSha}`,
                    '_blank'
                  )
                }}
              >
                {commitSha}
              </span>
            </p>
          </AdminOnlyRing>
          <div
            className="ml-4 flex h-[52px] items-center rounded-2xl bg-core-secondary px-4"
            data-testid="profile-account-type-pill"
          >
            <Person
              size={18}
              className="mr-1 stroke-fixed-accent-color"
              aria-hidden="true"
            />
            <strong className="text-label-medium text-core-on-secondary">
              {roleText}
            </strong>
          </div>
        </div>
      </Header>
      <Contents className="h-full overflow-hidden p-1">
        <Tabs.Root
          className="block h-full min-h-full w-full gap-5 md:flex"
          orientation="vertical"
          value={selectedPage}
          onValueChange={onChangeTab}
        >
          <ProfileNavigation
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
          />
          <Tabs.Content
            value={selectedPage}
            className="h-full w-full overflow-y-auto rounded-2xl border border-outline-light bg-core-tertiary p-5 md:p-10"
          >
            {breakoutUser.isLoading && (
              <div className="flex h-full w-full items-center justify-center">
                <Spinner size={3} />
              </div>
            )}

            {!breakoutUser.isLoading && (
              <>
                {selectedPage === ProfilePages.details && (
                  <ProfileInfoSection />
                )}
                {selectedPage === ProfilePages.transactions && (
                  <ProfileTransactions />
                )}
                {selectedPage === ProfilePages.avTest && <ProfileAvSection />}
                {selectedPage === ProfilePages.podcast && (
                  <div>
                    <PodcastsComponent
                      titleOverride={tt.profile.podcasts()}
                      contentClassName="md:!ml-0 md:!mt-0"
                      titleClassName="!mt-0 !mb-0 !text-headline-large"
                      subTextClassName="text-body-large text-core-on-tertiary mb-6 mt-1"
                    />
                    {rootStore.router.queryParams?.returnTo && (
                      <div className="mt-4 text-right">
                        <Button
                          onClick={() => {
                            const returnTo =
                              rootStore.router.queryParams?.returnTo
                            if (returnTo && typeof returnTo === 'string') {
                              window.location.href = returnTo
                            } else {
                              rootStore.navigateTo('home')
                            }
                          }}
                          className="mr-4 bg-core-primary text-core-on-primary"
                        >
                          {tt.common.back()}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {selectedPage === ProfilePages.promotions && (
                  <ProfilePromotions />
                )}
                {selectedPage === ProfilePages.scoreMappingDefaults && (
                  <ProfileScoreMappingDefaults />
                )}
                {selectedPage === ProfilePages.settings && <ProfileSettings />}
                {selectedPage === ProfilePages.viewFeatureFlags && (
                  <ProfileViewFeatureFlags cubit={cubit} />
                )}
                {selectedPage === ProfilePages.ltiSettings &&
                  allowLtiSettings && <ProfileLtiSettings cubit={cubit} />}
              </>
            )}
          </Tabs.Content>
        </Tabs.Root>
      </Contents>
    </MainPane>
  )
})
